import classNames from 'classnames';

import type { CidSummary } from '../types';
import { PlatformIcon } from './PlatformIcon';

interface AccountFilterProps {
  accounts: CidSummary[]; // accounts available for the current (template, platform) filter
  selected: ReadonlySet<string>; // CIDs currently selected
  onChange: (next: Set<string>) => void;
}

// Multi-select chip row: each chip is a CID. Click toggles. "All" pseudo-chip selects every
// account at once. Empty state when the upstream filter (template + platform) leaves no accounts.
export const AccountFilter = ({ accounts, selected, onChange }: AccountFilterProps) => {
  if (accounts.length === 0) {
    return (
      <div className='account-filter'>
        <span className='account-filter__label'>Accounts</span>
        <span className='account-filter__empty'>No accounts match the current filter.</span>
      </div>
    );
  }

  const allSelected = accounts.every(a => selected.has(a.cid));
  const selectAll = () => onChange(new Set(accounts.map(a => a.cid)));
  const toggle = (cid: string) => {
    const next = new Set(selected);
    if (next.has(cid)) {
      next.delete(cid);
    } else {
      next.add(cid);
    }
    onChange(next);
  };

  return (
    <div className='account-filter'>
      <span className='account-filter__label'>Accounts</span>
      <div className='account-filter__chips' role='group' aria-label='Account filter'>
        <button
          type='button'
          className={classNames(
            'account-filter__chip',
            'account-filter__chip--all',
            allSelected && 'account-filter__chip--active'
          )}
          onClick={selectAll}
        >
          All
        </button>
        {accounts.map(a => {
          const active = selected.has(a.cid);
          return (
            <button
              key={a.cid}
              type='button'
              aria-pressed={active}
              className={classNames(
                'account-filter__chip',
                `account-filter__chip--${a.platform}`,
                active && 'account-filter__chip--active'
              )}
              onClick={() => toggle(a.cid)}
            >
              <PlatformIcon platform={a.platform} size={12} />
              <span className='account-filter__chip-name'>{a.name}</span>
              {active && <span className='account-filter__chip-check'>✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};
